import { CarriersEnum, CarrierStatusEnum, SnackBarVariants, UserRole } from '@app/app.enum';
import { ICarrier, ICarriersListProps } from '@app/models';
import Injector from '@app/services';
import { AdminCarrierService } from '@app/services/admin-carrier.service';
import { MetaDataService } from '@app/services/meta-data.service';
import { SharedService } from '@app/services/shared.service';
import { GridList, GridListTile, GridListTileBar, Hidden, Typography } from '@material-ui/core';
import Axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { injectIntl } from 'react-intl';
import { isNullOrUndefined } from 'util';
import { AccountService } from '@app/services/account.service';
import withSnackBar from '../core/message.component';
import Ribbon from '../shared/ribbon.component';

const accountService: AccountService = Injector.get(AccountService);
const getClassNames = (selectedCarrier: number, currentCarrier: number): string => {
	return selectedCarrier === currentCarrier ? 'selected-carrier' : '';
};

const applyBackgroundColor = (carrierCode: CarriersEnum): string => {
	return `${carrierCode}-background`;
};

const metaDataService: MetaDataService = Injector.get(MetaDataService);
const sharedService: SharedService = Injector.get(SharedService);
const adminCarrierService: AdminCarrierService = Injector.get(AdminCarrierService);

const CarriersListComponent: React.FunctionComponent<ICarriersListProps> = (props) => {
	const [selectedCarrier, updateSelectedCarrier] = useState<number>(null);
	const [carriersList, setCarriersList] = useState<ICarrier[]>([]);

	useEffect(() => {
		let mounted = true;
		if (mounted) {
			fetchAllCarriers(props.userType);
		}
		return () => {
			mounted = false;
		};
	}, []);

	const fetchAllCarriers = useCallback(
		async (userType: UserRole) => {
			try {
				let result = null;
				// For edit view only selected open should be visible, instead of complete list
				if (props.new !== true && !isNullOrUndefined(props.selectedCarrierCode)) {
					const carrier = await metaDataService.getCarrierByCode(props.selectedCarrierCode);
					result = [carrier];
					props.onClick(carrier);
				} else {
					if (accountService.IsAdmin(userType)) {
						result = await adminCarrierService.getAllCarriersForAdmin(props.appliedSCAC);
					} else {
						result = await metaDataService.getAllCarriers(props.appliedSCAC);
					}
				}

				setCarriersList(result);
			} catch (err) {
				if (!Axios.isCancel(err)) {
					props.openSnackBar(sharedService.parseError(err.data), SnackBarVariants.Error);
				}
			}
		},
		[props.appliedSCAC]
	);

	const getCarrierImageTile = useCallback(
		(carrier: ICarrier, index: number) => {
			return (
				<GridListTile
					classes={{
						root: 'carrier-image',
						tile: `carriers-tile ${getClassNames(
							props.selectedCarrierCode === carrier.code ? index : selectedCarrier,
							index
						)}`,
					}}
					onClick={(e: React.MouseEvent) => {
						e.preventDefault();
						props.onClick(carrier);
						updateSelectedCarrier(index);
						if (!props.new && !!props.onClick) {
							props.onClick(carrier);
						}
					}}
					key={index}
				>
					{carrier?.status === CarrierStatusEnum.Private && (
						<Ribbon text={carrier.status} classNames='carrier-ribbon' />
					)}
					<div className={`carrier-img  ${applyBackgroundColor(carrier.code)}`}>
						<img src={carrier.logo} className='image-dimensions' />
					</div>
					<GridListTileBar
						classes={{ root: 'carrier-image-caption', titleWrap: 'm-0' }}
						subtitle={
							<Typography variant='caption' color='textSecondary' noWrap={true}>
								<strong>({carrier.code})</strong>
							</Typography>
						}
						title={
							<Typography variant='caption' color='textSecondary' noWrap={true}>
								<strong>{carrier.name}</strong>
							</Typography>
						}
					/>
				</GridListTile>
			);
		},
		[selectedCarrier]
	);

	const carriersListing = useMemo<JSX.Element[]>(() => {
		return carriersList.map((element: ICarrier, index: number) => {
			return getCarrierImageTile(element, index);
		});
	}, [carriersList, getCarrierImageTile]);

	return (
		<div>
			<Hidden only={['xs', 'sm']}>
				<GridList
					classes={{
						root: `carrier-list`,
					}}
					cols={5}
					spacing={16}
				>
					{carriersListing}
				</GridList>
			</Hidden>
			<Hidden only={['sm', 'md', 'lg', 'xl']}>
				<GridList
					classes={{
						root: `carrier-list`,
					}}
					cols={2}
					spacing={16}
				>
					{carriersListing}
				</GridList>
			</Hidden>
			<Hidden only={['xs', 'md', 'lg', 'xl']}>
				<GridList
					classes={{
						root: `carrier-list`,
					}}
					cols={3}
					spacing={16}
				>
					{carriersListing}
				</GridList>
			</Hidden>
		</div>
	);
};

export default withSnackBar(injectIntl(CarriersListComponent));



// WEBPACK FOOTER //
// ./src/components/carriers/carriers-list.component.tsx